import { GridControlled } from '@casops/components/tables/grid/GridControlled';
import { AssetDeviceTypeFormatter } from '@casops/components/tables/formatters/enumFormatters';
import { AssetApprovalConflictItemModel } from '@casops/store/apis/assetApprovalsApi';
import { ColDef, RowClassParams, RowClassRules } from 'ag-grid-community';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ConflictTable.module.scss';
import { lockColumn } from '@casops/components/tables/grid/columnDefs';

type Model = AssetApprovalConflictItemModel;

const sliceKey = 'AssetApprovalConflictItemModel';

// Add a CSS class to the reporting device row so that the cells are bold and red.
const rowClassRules: RowClassRules<Model> = {
  'dm-danger-row': (params: RowClassParams<Model>) => Boolean(params.data?.rep),
};

interface ConflictTableProps {
  conflicts: Model[];
}

export const ConflictTable = ({ conflicts }: ConflictTableProps): ReactElement => {
  const { t } = useTranslation();

  const columnDefs = useMemo<ColDef<Model>[]>(
    () => [
      { field: 'did', headerName: t('Fields.DeviceId'), minWidth: 150 },
      {
        field: 'hw',
        headerName: t('Fields.DeviceType'),
        valueFormatter: AssetDeviceTypeFormatter<Model, 'hw'>,
        minWidth: 110,
      },
      { field: 'dn', headerName: t('Fields.DeviceName'), minWidth: 120 },
      { field: 'an', headerName: t('Fields.AssetName'), minWidth: 120 },
      // Use the hidden 'rep' field to sort the rows so that the reporting device row is always displayed first.
      { field: 'rep', sort: 'desc', hide: true },
    ],
    [t],
  );

  const deviceNameMessage = useMemo<string | undefined>(() => {
    // Get the reporting row device name.
    const deviceName = conflicts.find(item => item.rep)?.dn;

    // Look for matches in the conflicts list.
    if (typeof deviceName !== 'undefined') {
      const hasDeviceNameMatch = conflicts.some(({ rep, dn }) => !rep && dn === deviceName);

      // If there are device names that match the reporting device name, return a message.
      if (hasDeviceNameMatch) {
        return t('DeviceNameMatch', { name: deviceName });
      }
    }

    return undefined;
  }, [t, conflicts]);

  return (
    <div className={styles.container}>
      <GridControlled<Model>
        sliceKey={sliceKey}
        rowData={conflicts}
        columnDefs={columnDefs}
        defaultColDef={lockColumn}
        rowClassRules={rowClassRules}
        autoheight
      />
      {deviceNameMessage && <div className={styles.message}>{deviceNameMessage}</div>}
    </div>
  );
};

export default ConflictTable;
